import { DEFAULT_CHART_COLORS, formatData } from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { parseRichTextToPptRuns } from '../helpers.js';
import {
  CHART_LABEL_NEGATIVE_COLOR,
  CHART_LABEL_POSITIVE_COLOR,
  FONT_FACE,
  FOOTER_TEXT,
  KEY_FINDINGS_TEXT,
  LABEL_COLOR,
  LINE_COLOR,
  READ_AS_TEXT,
  SURVEY_TITLE_OPTIONS,
  TEXT_COLOR,
  TITLE_TEXT,
} from '../constants';

/**
 * transforms and formats chart data format to pptxGen format
 * @param {String} chartDataFormat
 * @return {String}
 */
export const transformToPptxGenFormat = (chartDataFormat) => {
  switch (chartDataFormat) {
    case '+0.0':
      return '+0.0';
    case '0,0':
      return '#,##0';
    case '0,0.0':
      return '#,##0.0';
    case '0,0.0%':
      return '#,##0.0%';
    case '0.0':
      return '0.0';
    case '0.0%':
      return '0.0%';
    case '0.00%':
      return '0.00%';
    case '$0,0':
      return '$#,##0';
    case '$0,0.00':
      return '$#,##0.00';
    default:
      return '#,##0';
  }
};

/**
 * Gets chart data
 * @param {Object} chart
 * @returns {Object}
 */
const getChartData = (chart) => {
  if (!chart || !Array.isArray(chart.series)) return null;

  chart.series.forEach((series) => {
    series.data.forEach((data, index) => {
      if (data === 'NAN_SENTINEL') {
        series.data[index] = null;
      }
    });
  });

  const chartSeriesLength = chart.series.length;
  const seriesLength = chart.series?.[0]?.data?.length || 0;
  const labels = chart.xAxis?.categories?.slice(0, seriesLength) || [];
  const isHorizontalBar = chart?.barDir === 'bar';
  const reverseIfNeeded = (array, isPercentStacked = false) =>
    isHorizontalBar && !isPercentStacked ? [...array].reverse() : array;
  const dataSeries = reverseIfNeeded(chart?.series, chart?.barGrouping === 'percentStacked')?.map(
    (series) => ({
      labels: reverseIfNeeded(labels),
      name: series.name,
      values: reverseIfNeeded(series.data || []),
    })
  );

  const maxVal = Math.max(...chart.series.flatMap((s) => s.data)) || 0;
  const format = transformToPptxGenFormat(chart.format);

  return {
    chartOptions: {
      barDir: 'col',
      barGapWidthPct: 50,
      barOverlapPct: -10,
      catAxisLabelColor: LABEL_COLOR,
      catAxisLabelFontSize: 7,
      catAxisLabelRotate: -45,
      catAxisLabelWrap: true,
      catAxisLineShow: false,
      chartColors: DEFAULT_CHART_COLORS.slice(0, chartSeriesLength || 1),
      dataLabelFontSize: 6,
      dataLabelFormatCode: format,
      h: '75%',
      legendColor: TEXT_COLOR,
      legendFontSize: 12,
      legendPos: 't',
      showDataLabels: true,
      showLegend: chartSeriesLength > 1,
      showTitle: false,
      showValue: true,
      valAxisLabelColor: LABEL_COLOR,
      valAxisLabelFontSize: 8,
      valAxisLabelFormatCode: format,
      valAxisLineShow: false,
      valAxisMaxVal: maxVal * 1.2,
      valAxisMinVal: 0,
      valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
      w: '75%',
      x: 1,
      y: 0.8,
    },
    dataSeries,
  };
};

/**
 * Filters the data series to hide small % series' values < 0.05%, applicable to stacked bar charts
 * @param {Array[Object]} dataSeries
 * @returns {Array[Object]}
 */
const filterDataSeries = (dataSeries) => {
  return dataSeries.map((series) => ({
    ...series,
    values: series?.values?.map((value) => {
      const numericValue = typeof value === 'object' && value?.y != null ? value.y : value;

      return typeof numericValue === 'number' && numericValue >= 0.0005 ? numericValue : null;
    }),
  }));
};

/**
 * Creates chart slide
 * @param {Object} chart
 * @param {String} brokenBy
 * @param {Object} pptx
 * @param {String} readAs
 * @param {String} reportUrl
 * @param {Array} reportDateRange
 * @param {String} reportType
 * @param {String} slideTitle
 * @returns {Object}
 */
export const createChartSlide = ({
  brokenBy,
  chart,
  pptx,
  readAs = null,
  reportDateRange,
  reportType,
  reportUrl,
  slideTitle = null,
}) => {
  const chartData = getChartData(chart);
  if (!chartData) return null;

  const slide = pptx.addSlide();
  const title = chart?.series[1]?.name || chart?.series[0]?.name;

  slide.addText(slideTitle || `${title} by ${brokenBy}`, {
    ...TITLE_TEXT,
    fontSize: slideTitle ? 14 : TITLE_TEXT.fontSize,
  });
  if (readAs)
    slide.addText(readAs, {
      ...READ_AS_TEXT,
      w: '20%',
      x: 7.5,
    });

  const isPercentStacked = chart?.barGrouping === 'percentStacked';
  const filteredDataSeries = filterDataSeries(chartData.dataSeries);

  const adjustedChartOptions = {
    ...chartData.chartOptions,
    barDir: chart?.barDir,
    barGrouping: chart?.barGrouping,
    showLegend: readAs ? true : chart?.series.length > 1,
    valAxisMaxVal: isPercentStacked ? 1.0 : chartData.chartOptions.valAxisMaxVal,
    x: readAs || chart?.barDir === 'bar' ? 0.2 : chartData.chartOptions?.x,
    y: slideTitle && readAs && chart?.barDir === 'bar' ? 0.8 : chartData.chartOptions?.y,
  };

  if (chart?.type === 'bar-chart-two-labels') {
    const data = chart.series?.[0]?.data || [];
    const rows = data.map((item) => [
      {
        options: {
          align: 'center',
          bold: true,
          color:
            item.yDelta > 0
              ? CHART_LABEL_POSITIVE_COLOR
              : item.yDelta < 0
                ? CHART_LABEL_NEGATIVE_COLOR
                : TEXT_COLOR,
          fontFace: FONT_FACE,
          fontSize: 8,
          valign: 'middle',
        },
        text:
          item.y2 == null ||
          item.y2 === 'INF_SENTINEL' ||
          item.y2 === 'NAN_SENTINEL' ||
          (typeof item.y2 === 'number' && Number.isNaN(item.y2))
            ? '-'
            : formatData({ format: '0.0', value: item.y2 }),
      },
    ]);

    slide.addTable(rows, {
      colW: [0.5],
      h: '61%',
      w: 1,
      x: 7,
      y: 1.31,
    });

    slide.addChart(pptx.charts.BAR, filteredDataSeries, { ...adjustedChartOptions, w: '70%' });

    addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, slide });

    return slide;
  }

  slide.addChart(pptx.charts.BAR, filteredDataSeries, adjustedChartOptions);

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, slide });

  return slide;
};

/**
 * Creates chart slide for survey dashboard PowerPoint export
 * @param {Object} options
 * @param {Object} options.chart
 * @param {String} options.compareBy
 * @param {Object} options.pptx
 * @param {String} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.keyFindings
 * @param {String} options.title
 * @returns {Object}
 */
export const createSurveyChartSlide = ({
  chart,
  compareBy,
  keyFindings,
  pptx,
  questionNumber,
  questionTitle,
  reportDateRange,
  reportType,
  reportUrl,
  title,
}) => {
  const chartData = getChartData(chart);
  if (!chartData) return null;

  const questionTitleRuns = parseRichTextToPptRuns(questionTitle);
  if (!questionTitleRuns.length) return null;

  const slide = pptx.addSlide();

  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const compareByLabel = compareBy?.label || compareBy?.value || compareBy;

  const titleRuns = [
    ...(questionPrefix ? [{ text: questionPrefix }] : []),
    ...questionTitleRuns,
    ...(compareByLabel ? [{ text: ` Compared by: ${compareByLabel}` }] : []),
  ];

  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(titleRuns, titleOptions);

  const chartYPosition = titleOptions.h > 1.0 ? 1.4 : 1.1;
  const hasKeyFindings = keyFindings && Array.isArray(keyFindings) && keyFindings.length > 0;

  const chartHeight = hasKeyFindings ? '65%' : titleOptions.h > 1.0 ? '58%' : '62%';
  const chartWidth = hasKeyFindings ? '55%' : '75%';
  const chartXPosition = hasKeyFindings ? 0.5 : 1.0;

  const adjustedChartOptions = {
    ...chartData.chartOptions,
    barDir: 'col',
    h: chartHeight,
    showDataLabels: true,
    showLegend: chartData.dataSeries.length > 1,
    valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
    w: chartWidth,
    x: chartXPosition,
    y: chartYPosition,
  };

  slide.addChart(pptx.charts.BAR, chartData.dataSeries, adjustedChartOptions);

  if (hasKeyFindings) {
    const keyFindingsText =
      'Key findings:\n' +
      keyFindings
        .map((finding) => {
          let text = (finding.output || '').toString().trim();
          text = text.replace(/^[•·*]\s*/, '');
          return `• ${text}`;
        })
        .filter((text) => text.length > 2)
        .join('\n');

    if (keyFindingsText !== 'Key Findings:\n') {
      slide.addText(keyFindingsText, {
        ...KEY_FINDINGS_TEXT,
        fontSize: 12,
        h: 1.66,
        lineSpacing: 18,
        margin: [0.15, 0.15, 0.15, 0.15],
        valign: 'top',
        w: 3.0,
        wrap: true,
        x: 6.5,
        y: 1.5,
      });
    }
  }

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, slide });
  slide.addText(
    [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
    FOOTER_TEXT
  );

  return slide;
};

/**
 * Returns structured data for scatter charts
 * @param {Object} chart
 * @returns {Object}
 */
const getScatterData = (chart) => {
  if (!chart || !Array.isArray(chart.series)) return null;

  chart.series.forEach((series) => {
    series.data.forEach((data, index) => {
      if (data === 'NAN_SENTINEL') {
        series.data[index] = null;
      }
    });
  });

  const formatY = transformToPptxGenFormat(chart.formatY);
  const dataSeries = [];
  const allXValues = [];
  const allYValues = [];

  chart.series?.forEach((series) => {
    const yValues = [];
    const labels = [];
    series.data?.forEach((point) => {
      if (point && point.x !== undefined) {
        allXValues.push(point.x);
      }
      if (point && point.y !== undefined) {
        yValues.push(point.y);
        labels.push(point.name);
      }
    });

    if (allXValues.length > 0) {
      dataSeries.push({
        name: chart.nameX,
        values: allXValues,
      });
    }
    if (yValues.length > 0) {
      dataSeries.push({
        labels: labels,
        name: chart.nameY,
        values: yValues,
      });
      allYValues.push(...yValues);
    }
  });

  return {
    chartOptions: {
      catAxisHidden: false,
      catAxisLabelColor: LABEL_COLOR,
      catAxisLabelFontSize: 8,
      catAxisLabelWrap: true,
      catAxisLineShow: true,
      catAxisMaxVal: Math.min(1, Math.max(...allXValues) * 1.1),
      catAxisMinVal: 0,
      catAxisTitle: chart.nameX,
      catGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
      chartColors: DEFAULT_CHART_COLORS.slice(0, dataSeries.length),
      dataLabelFontSize: 6,
      dataLabelPosition: 't',
      h: '70%',
      legendColor: TEXT_COLOR,
      legendFontSize: 10,
      legendPos: 't',
      lineSize: 0,
      showCatAxisTitle: true,
      showLabel: true,
      showLegend: false,
      showTitle: false,
      showValAxisTitle: true,
      valAxisLabelColor: LABEL_COLOR,
      valAxisLabelFontSize: 8,
      valAxisLabelFormatCode: formatY,
      valAxisLineShow: false,
      valAxisMinVal: Math.max(0, Math.min(...allYValues) * 0.95),
      valAxisTitle: chart.nameY,
      valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
      w: '75%',
      x: 0.2,
      y: 1.1,
    },
    dataSeries,
  };
};

/**
 * Creates scatter chart slide with footer text and title
 * @param {String} brokenBy
 * @param {Object} chart
 * @param {Object} pptx
 * @param {String} readAs
 * @param {String} reportUrl
 * @param {Array} reportDateRange
 * @param {String} reportType
 * @param {String} slideTitle
 * @returns
 */
export const createScatterChartSlide = ({
  chart,
  pptx,
  readAs = null,
  reportDateRange,
  reportType,
  reportUrl,
  slideTitle = null,
}) => {
  const chartData = getScatterData(chart);
  if (!chartData) return null;

  const slide = pptx.addSlide();

  slide.addText(slideTitle, { ...TITLE_TEXT, fontSize: slideTitle ? 14 : TITLE_TEXT.fontSize });
  if (readAs)
    slide.addText(readAs, {
      ...READ_AS_TEXT,
      w: '20%',
      x: 7.5,
    });

  slide.addChart(pptx.charts.SCATTER, chartData.dataSeries, chartData.chartOptions);

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, slide });

  return slide;
};
